import axios, { AxiosRequestConfig, AxiosInstance } from 'axios'
import { get } from 'lodash'

import { encodeBody, decodeToken, isTokenExpired } from 'utils/auth/helpers'
import { RuntimeStorageManager } from 'utils/auth/RuntimeStorageManager'
import { TOKENS_TYPES } from 'utils/auth/constants'
import { config } from 'lib/config'
import { SetTokenParams, TokenResponse } from 'types/authManager'

export const getHostName = () => {
  const hostname = get(window, 'location.hostname')

  return hostname && hostname.match('localhost') ? 'http://localhost:3000' : `https://${hostname}`
}

const REALM_NAME = 'orion'

export const keycloakConfig = {
  baseURL: `https://keycloak.${config.API_BASE_URL}/auth/realms/${REALM_NAME}/protocol/openid-connect`,
  clientId: 'hris-frontend',
}

export const tokenClient: AxiosInstance = axios.create({
  baseURL: keycloakConfig.baseURL,
  responseType: 'json',
  timeout: 10000,
  headers: {
    'Content-type': 'application/x-www-form-urlencoded',
  },
})

export const getRenewAccessTokenConfig = (refreshToken: string): AxiosRequestConfig => ({
  url: '/token',
  method: 'post',
  withCredentials: true,
  data: encodeBody({
    grant_type: 'refresh_token',
    client_id: keycloakConfig.clientId,
    refresh_token: refreshToken,
  }),
})

class AuthManager {
  storage: RuntimeStorageManager
  authenticated: boolean
  token: string | null
  idToken: string | null
  tokenParsed: string | null
  refreshToken: string | null
  refreshTokenParsed: string | null
  updateTokenPromise: Promise<boolean> | null

  constructor() {
    this.storage = new RuntimeStorageManager()
    this.authenticated = false
    this.token = null
    this.idToken = null
    this.tokenParsed = null
    this.refreshToken = null
    this.refreshTokenParsed = null
    this.updateTokenPromise = null

    this.isAccessTokenExpired = this.isAccessTokenExpired.bind(this)
    this.isRefreshTokenExpired = this.isRefreshTokenExpired.bind(this)
    this.clearAuthentication = this.clearAuthentication.bind(this)
  }

  init(): Promise<void> {
    this.storeJobPostingQueryParams()

    return this._checkIsLoggedIn()
  }

  /**
   * Temporarily clears auth data until page is reloaded
   */
  clearAuthentication(): void {
    this.authenticated = false
    this.token = null
    this.tokenParsed = null
    this.refreshToken = null
    this.refreshTokenParsed = null
  }

  getToken(): string | null {
    return this.token
  }

  isAccessTokenExpired(): boolean {
    return isTokenExpired(get(this.tokenParsed, 'exp'))
  }

  isRefreshTokenExpired(): boolean {
    return isTokenExpired(get(this.refreshTokenParsed, 'exp'))
  }

  base64urlEncode(arrayBuffer) {
    return btoa(String.fromCharCode(...arrayBuffer))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '')
  }

  generateCodeVerifier() {
    const array = new Uint8Array(32)

    window.crypto.getRandomValues(array)

    return this.base64urlEncode(array)
  }
  exchangeCodeForToken({ code, codeVerifier }) {
    const { baseURL, clientId } = keycloakConfig

    const params = {
      grant_type: 'authorization_code',
      client_id: clientId,
      code: code,
      redirect_uri: getHostName(),
      code_verifier: codeVerifier,
    }

    return fetch(`${baseURL}/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(params),
    }).then(resp => {
      if (!resp.ok) throw new Error('Token exchange failed')

      return resp.json()
    })
  }

  // NOTE: refactor. We need to move this out of AuthManager
  storeJobPostingQueryParams() {
    const url = new URL(window.location.href)
    const params = url.searchParams
    const masterId = params.get('masterId')
    const countyId = params.get('countyId')
    const districtId = params.get('districtId')

    // Set params if we have at least one. We have them when get redirected from Jobs portal
    if (masterId || countyId || districtId) {
      // if there is item, set it, otherwise - remove it.
      // Because for example districtId that could be present for prev application but not for this one
      masterId ? localStorage.setItem('masterId', masterId) : localStorage.removeItem('masterId')
      countyId ? localStorage.setItem('countyId', countyId) : localStorage.removeItem('countyId')
      districtId ? localStorage.setItem('districtId', districtId) : localStorage.removeItem('districtId')
    }
  }

  generateCodeChallenge(codeVerifier) {
    const encoder = new TextEncoder()
    const data = encoder.encode(codeVerifier)

    return window.crypto.subtle.digest('SHA-256', data).then(digest =>
      btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, ''),
    )
  }

  redirectToPromptLogin(codeChallenge) {
    const authUrl =
      `${keycloakConfig.baseURL}/auth` +
      `?response_type=code` +
      `&client_id=${keycloakConfig.clientId}` +
      `&redirect_uri=${getHostName()}` +
      `&scope=openid` +
      `&code_challenge=${codeChallenge}` +
      `&code_challenge_method=S256` +
      `&prompt=none` // This forces Keycloak to skip the session cache and re-authenticate at the identity provider

    window.location.href = authUrl
  }

  private _checkIsLoggedIn(): Promise<void> {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const error = params.get('error')
    const noActiveKeycloakSession = error === 'login_required'

    if (code) {
      const codeVerifier = localStorage.getItem('pkce_verifier')

      return AuthManagerInstance.exchangeCodeForToken({ code, codeVerifier })
        .then(res => {
          this.authenticated = true

          AuthManagerInstance._setToken({
            accessToken: res?.access_token || '',
            refreshToken: res?.refresh_token || '',
            idToken: res?.id_token || '',
          })

          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname)
        })
        .finally(() => Promise.resolve())
    } else if (noActiveKeycloakSession) {
      return Promise.resolve()
    } else {
      const verifier = AuthManagerInstance.generateCodeVerifier()

      localStorage.setItem('pkce_verifier', verifier)

      AuthManagerInstance.generateCodeChallenge(verifier).then(challenge =>
        AuthManagerInstance.redirectToPromptLogin(challenge),
      )
    }
  }

  updateTokens(): Promise<boolean> {
    if (this.authenticated && this.isAccessTokenExpired()) {
      if (this.isRefreshTokenExpired()) {
        this.authenticated = false

        return Promise.resolve(false)
      } else {
        return this._renewTokens().catch(() => {
          this.authenticated = false

          return Promise.resolve(false)
        })
      }
    } else {
      return Promise.resolve(true)
    }
  }

  private _renewTokens(): Promise<boolean> {
    this.updateTokenPromise = new Promise((resolve, reject) => {
      tokenClient
        .request(getRenewAccessTokenConfig(this.refreshToken))
        .then(({ data }: { data: TokenResponse }) => {
          this._setToken({ accessToken: data.access_token, refreshToken: data.refresh_token, idToken: data.id_token })
          resolve(true)
        })
        .catch(err => reject(err))
        .finally(() => {
          this.updateTokenPromise = null
        })
    })

    return this.updateTokenPromise
  }

  private _setToken({ accessToken, refreshToken, idToken }: SetTokenParams): void {
    if (accessToken) {
      this.token = accessToken
      this.tokenParsed = decodeToken(accessToken)
      this.storage.setFieldInStorage({ name: TOKENS_TYPES.ACCESS_TOKEN, value: accessToken })
    }

    if (refreshToken) {
      this.refreshToken = refreshToken
      this.refreshTokenParsed = decodeToken(refreshToken)
      this.storage.setFieldInStorage({ name: TOKENS_TYPES.REFRESH_TOKEN, value: refreshToken })
    }

    if (idToken) {
      this.idToken = idToken
      this.storage.setFieldInStorage({ name: TOKENS_TYPES.ID_TOKEN, value: idToken })
    }
  }
}

export const AuthManagerInstance = new AuthManager()
